/**
 * RentMatch color constants — mapped onto the Rentio design system.
 *
 * The RentMatch UI was originally built with a Tailwind-style palette. Every
 * one of those ad-hoc hex values has been mapped here to the nearest
 * `@rentiohq/web-theme` token value (the `theme: ...` note shows which one), so
 * the feature now renders with Rentio's actual colors instead of foreign ones.
 *
 * Names are kept feature-local (and a few near-duplicates remain) so the call
 * sites did not have to change; functionally these now resolve to ~30 distinct
 * Rentio token colors instead of 50 Tailwind ones. The only non-token color
 * is `brand` — the RentMatch wordmark, which is a brand asset, not a theme hue.
 */
export const RM_COLORS = {
  // ── Brand (RentMatch asset — intentionally not a theme hue) ─────────────
  brand: "#FF3A59",
  brandAccent: "#EC4899",

  // ── Neutrals / grays → theme neutral & gray ─────────────────────────────
  white: "#FFFFFF", // theme: neutral0
  gray50: "#F7F7F7", // theme: neutral10
  gray50Alt: "#F7F7F7", // theme: neutral10
  gray100: "#EEEEEE", // theme: neutral20
  gray200: "#E2E2E2", // theme: neutral30
  gray300: "#D0D0D0", // theme: neutral40
  gray400: "#989898", // theme: neutral60
  gray500: "#7D7D7D", // theme: neutral70
  gray700: "#494949", // theme: neutral90
  gray800: "#2C2C2C", // theme: neutral100
  gray900: "#2C2C2C", // theme: neutral100
  border: "#E2E2E2", // theme: neutral30
  borderAlt: "#E2E2E2", // theme: neutral30
  slate: "#303332", // theme: gray 1000
  grayMuted: "#A7A9A8", // theme: gray 400
  grayMutedAlt: "#A7A9A8", // theme: gray 400

  // ── Greens → theme green / primary ──────────────────────────────────────
  green: "#17A680", // theme: green 800
  greenStrong: "#0D8B69", // theme: green 1000
  greenDark: "#0D8B69", // theme: green 1000
  greenDarker: "#0D8B69", // theme: green 1000
  greenDeep: "#0D8B69", // theme: green 1000
  greenBg: "#F1F9F6", // theme: green 0
  greenBgAlt: "#F1F9F6", // theme: green 0
  greenBgSoft: "#F1F9F6", // theme: green 0
  greenBorder: "#ADDDD0", // theme: green 200
  rentmatchGreen: "#17A680", // theme: green 800
  teal: "#17A680", // theme: green 800

  // ── Ambers / yellows / orange → theme yellow / orange ───────────────────
  amber: "#FFAB00", // theme: yellow 800
  amberStrong: "#E69100", // theme: yellow 1000
  amberDark: "#E69100", // theme: yellow 1000
  yellowDark: "#E69100", // theme: yellow 1000
  amberBorder: "#FAE978", // theme: yellow 400
  amberBg: "#FCFDE7", // theme: yellow 0
  yellowBg: "#FCFDE7", // theme: yellow 0
  yellowBgSoft: "#FCFDE7", // theme: yellow 0
  orange: "#FF8C00", // theme: orange 1000

  // ── Reds → theme red ────────────────────────────────────────────────────
  red: "#EF3D59", // theme: red 800
  redAlt: "#EF3D59", // theme: red 800
  redDark: "#BF0C3D", // theme: red 1000
  redBg: "#FEF2F1", // theme: red 0

  // ── Blues → theme blue ──────────────────────────────────────────────────
  blue: "#2D9CDB", // theme: blue 800
  blueStrong: "#09527B", // theme: blue 1000
  blueDark: "#09527B", // theme: blue 1000
  blueDeep: "#09527B", // theme: blue 1000
  blueBg: "#F1FAFC", // theme: blue 0
  blueBgAlt: "#C2E6F1", // theme: blue 200
  skyBg: "#F1FAFC", // theme: blue 0

  // ── Purple → theme purple ───────────────────────────────────────────────
  purple: "#777BAE", // theme: purple 0
} as const;
